import BttfAxios from '@features/utils/axios';
import IPostTrade from '@market/interfaces/IPostTrade';
import IDocumentData from '@market/interfaces/IDocumentData';
import axios, { AxiosResponse } from 'axios';

class SecondaryMarketServiceClass {

  private config = { headers: { 'Cache-Control': 'no-cache' } };

  public getApiSettings() {
    return BttfAxios.get(`/api/settings`, (res) => {
      return Promise.resolve(res);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public getAssets(): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/assets`, this.config);
  }

  public getSPVs(): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/spvs`, this.config);
  }

  public getAsset(assetId: number): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/assets/${assetId}`, this.config);
  }

  public getSPV(spvId: number): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/spvs/${spvId}`, this.config);
  }

  public getSPVInvestorDetails(spvId: number): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/spvs/${spvId}/investor_details`, this.config);
  }

  public getBuyerAgreements(documentData: IDocumentData): Promise<AxiosResponse<any>> {
    const { tradeData, assetId } = documentData;
    if (!tradeData) return Promise.reject('No required data');

    const params = `trade_data[number_of_shares]=
      ${tradeData.numberOfShares}&trade_data[price_per_share]=${tradeData.pricePerShare}`;
    return axios.get(`/api/share_service/assets/${assetId}/buyer_agreements?${params}`);
  }

  public getSellerAgreements(documentData: IDocumentData): Promise<AxiosResponse<any>> {
    const { orderData, assetId } = documentData;
    if (!orderData) return Promise.reject('No required data');

    const params = `order_data[number_of_shares]=
      ${orderData.numberOfShares}&order_data[price_per_share]=${orderData.pricePerShare}`;
    return axios.get(`/api/share_service/assets/${assetId}/seller_agreements?${params}`);
  }

  public getAgreement(tradeId:number) {
    return axios.get(`/api/share_service/trades/${tradeId}/agreement`);
  }

  public getMyTrades(page: number = 1, perPage: number = 10): Promise<AxiosResponse<any>> {
    const params = BttfAxios.paramBuilder({ per_page: perPage, page: page });
    return axios.get(`/api/share_service/trades${params}`, this.config);
  }

  public getAssetOrders(assetId: number): Promise<AxiosResponse<any>> {
    return BttfAxios.all([
      SecondaryMarketService.getSellOrders(assetId),
      SecondaryMarketService.getBuyOrders(assetId),
    ], (sellOrders, buyOrders) => ({ sellOrders, buyOrders }));
  }

  public getSellOrders(assetId: number): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/assets/${assetId}/orders?type=sell`, this.config);
  }

  public getBuyOrders(assetId: number): Promise<AxiosResponse<any>> {
    return axios.get(`/api/share_service/assets/${assetId}/orders?type=buy`, this.config);
  }

  public postOrder(assetId: number, data: any): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/assets/${assetId}/orders`, data, (resp) => {
      return Promise.resolve(resp);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public postSellOrder(assetId: number, data: any): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/assets/${assetId}/sell/orders`, data, (resp) => {
      return Promise.resolve(resp);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public postBuyOrder(assetId: number, data: any): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/assets/${assetId}/buy/orders`, data, (resp) => {
      return Promise.resolve(resp);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public postBuyTrade(orderId: number, data: any): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/orders/${orderId}/buy`, data, (resp) => {
      return Promise.resolve(resp);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public postSellTrade(orderId: number, data: any): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/orders/${orderId}/sell`, data, (resp) => {
      return Promise.resolve(resp);
    }, (err) => {
      return Promise.reject(err);
    });
  }


  public purchaseOrder(assetId: number, orderId: number, data: IPostTrade): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/orders/${orderId}/purchase`, data, (res) => {
      return Promise.resolve(res);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public getAssetOrder(assetId: number, orderId: number) {
    return BttfAxios.get(`/api/share_service/assets/${assetId}/orders/${orderId}`, (res) => {
      return Promise.resolve(res);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public getAssetShares(assetId: number) {
    return axios.get(`/api/share_service/assets/${assetId}/shares`, this.config);
  }

  public getWallets(investorId: number, currency: string = '') {
    let params = '';

    if (currency) {
      params = BttfAxios.paramBuilder({ currency });
    }

    return axios.get(`/api/investors/${investorId}/wallets/${params}`, this.config);
  }

  public getExchange() {
    return axios.get(`/api/exchange_rates`, this.config);
  }

  public getMyOrders() {
    return BttfAxios.get(`/api/share_service/orders`, (resp) => {
      return resp;
    });
  }

  public getTradesHistory(assetId: number) {
    return BttfAxios.get(`/api/share_service/assets/${assetId}/trades`, (resp) => {
      return resp;
    });
  }

  public getPublicTradesHistory(assetId: number) {
    return BttfAxios.get(`/api/share_service/assets/${assetId}/recent_trades`, (resp) => {
      return resp;
    });
  }

  public cancelSellOrder(orderId: number): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/sell/orders/${orderId}/cancel`, undefined, (res) => {
      return Promise.resolve(res);
    }, (err) => {
      return Promise.reject(err);
    });
  }

  public cancelBuyOrder(orderId: number): Promise<AxiosResponse<any>> {
    return BttfAxios.post(`/api/share_service/buy/orders/${orderId}/cancel`, undefined, (res) => {
      return Promise.resolve(res);
    }, (err) => {
      return Promise.reject(err);
    });
  }
}

const SecondaryMarketService = new SecondaryMarketServiceClass();

export default SecondaryMarketService;
